import type {
  AiBillingBreakdown,
  AiChatAttachmentDescriptor,
  AiChatCompletionResponse,
  AiChatCompletionUsage,
  AiChatStoredMessage,
} from '@ignite/types';
import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentGroup,
  AttachmentMedia,
  AttachmentTitle,
  Badge,
  Button,
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
  Separator,
  Skeleton,
  Spinner,
  Textarea,
} from '@ignite/ui';
import {
  AlertCircle,
  ArrowUp,
  Check,
  ChevronDown,
  Code2,
  Copy,
  File,
  FileArchive,
  FileAudio,
  FileCode2,
  FileImage,
  FileText,
  FileVideo,
  History,
  LoaderCircle,
  Maximize2,
  MessageCircle,
  Minimize2,
  Minus,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCcw,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import {
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
  type TransitionEvent as ReactTransitionEvent,
} from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import { useTranslation } from 'react-i18next';
import remarkGfm from 'remark-gfm';
import { useCopyAction } from '../../../lib/clipboard';
import { Link } from '@tanstack/react-router';
import { formatBytes, chatFailureKind, type ChatAttachmentItem } from './chat-state';
import { useChat, type ChatDisplayMessage } from './ChatProvider';

const CHAT_NARROW_QUERY = '(max-width: 680px)';
const CHAT_FLIP_TARGET_SELECTOR = '[data-global-chat-flip]';
const CHAT_HISTORY_OWNED_OVERLAY_SELECTOR = '[data-global-chat-history-owned-overlay]';
const CHAT_SURFACE_TRANSITION_MS = 225;
const CHAT_SURFACE_EASING = 'cubic-bezier(0.77, 0, 0.175, 1)';
const HISTORY_DRAWER_TRANSITION_MS = 225;
const HISTORY_DRAWER_REDUCED_TRANSITION_MS = 160;
const CHAT_COPY_FEEDBACK_RESET_MS = 2_000;
const CHAT_COMPOSER_TEXTAREA_MIN_HEIGHT = 64;
const CHAT_COMPOSER_TEXTAREA_MAX_HEIGHT = 160;

type PendingFocusIntent = {
  generation: number;
  expectedOpen: boolean;
  activeElementAtRequest: Element | null;
  resolveTarget: () => HTMLElement | null;
  frame: number | null;
};

export function GlobalChat() {
  const { t } = useTranslation();
  const chat = useChat();
  const setHistoryOpen = chat.setHistoryOpen;
  const narrow = useNarrowViewport();
  const [historyMounted, setHistoryMounted] = useState(false);
  const [historyTransitionOpen, setHistoryTransitionOpen] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const { expanded, setExpandedWithFlip, toggleExpandedWithFlip } =
    useExpandedChatSurface(surfaceRef);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const historyTriggerRef = useRef<HTMLButtonElement>(null);
  const historyCloseRef = useRef<HTMLButtonElement>(null);
  const expandTriggerRef = useRef<HTMLButtonElement>(null);
  const historyRef = useRef<HTMLElement>(null);
  const historyFocusReturnRef = useRef<'trigger' | 'messages' | null>(null);
  const restoreLauncherFocusRef = useRef(false);
  const openTargetRef = useRef(chat.open);
  const focusIntentRef = useRef<{
    generation: number;
    pending: PendingFocusIntent | null;
  }>({ generation: 0, pending: null });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  openTargetRef.current = chat.open;

  const cancelPendingFocusIntent = useCallback((expectedGeneration?: number) => {
    const pending = focusIntentRef.current.pending;
    if (
      !pending ||
      (expectedGeneration !== undefined && pending.generation !== expectedGeneration)
    ) {
      return;
    }
    if (pending.frame !== null) window.cancelAnimationFrame(pending.frame);
    focusIntentRef.current.pending = null;
  }, []);

  const attemptPendingFocusIntent = useCallback(
    (generation: number, fenceActiveElement: boolean) => {
      const focusIntent = focusIntentRef.current;
      const pending = focusIntent.pending;
      if (!pending || pending.generation !== generation) return;
      if (openTargetRef.current !== pending.expectedOpen) {
        cancelPendingFocusIntent(generation);
        return;
      }
      const target = pending.resolveTarget();
      if (!target?.isConnected || target.closest('[inert], [aria-hidden="true"]') !== null) {
        return;
      }
      const activeElement = document.activeElement;
      if (
        fenceActiveElement &&
        activeElement instanceof HTMLElement &&
        activeElement !== document.body &&
        activeElement !== pending.activeElementAtRequest &&
        activeElement !== target &&
        activeElement.isConnected &&
        activeElement.closest('[inert], [aria-hidden="true"]') === null
      ) {
        cancelPendingFocusIntent(generation);
        return;
      }
      target.focus();
      if (document.activeElement === target) cancelPendingFocusIntent(generation);
    },
    [cancelPendingFocusIntent],
  );

  const schedulePendingFocusIntent = useCallback(
    (generation: number) => {
      const pending = focusIntentRef.current.pending;
      if (!pending || pending.generation !== generation || pending.frame !== null) return;
      let completedSynchronously = false;
      const frame = window.requestAnimationFrame(() => {
        completedSynchronously = true;
        const current = focusIntentRef.current.pending;
        if (!current || current.generation !== generation) return;
        current.frame = null;
        attemptPendingFocusIntent(generation, true);
      });
      const current = focusIntentRef.current.pending;
      if (!completedSynchronously && current?.generation === generation) current.frame = frame;
    },
    [attemptPendingFocusIntent],
  );

  const requestTargetFocus = useCallback(
    (expectedOpen: boolean, resolveTarget: () => HTMLElement | null) => {
      const focusIntent = focusIntentRef.current;
      const generation = focusIntent.generation + 1;
      cancelPendingFocusIntent();
      focusIntent.generation = generation;
      focusIntent.pending = {
        generation,
        expectedOpen,
        activeElementAtRequest: document.activeElement,
        resolveTarget,
        frame: null,
      };
      attemptPendingFocusIntent(generation, false);
      schedulePendingFocusIntent(generation);
      return () => cancelPendingFocusIntent(generation);
    },
    [attemptPendingFocusIntent, cancelPendingFocusIntent, schedulePendingFocusIntent],
  );

  useLayoutEffect(() => () => cancelPendingFocusIntent(), [cancelPendingFocusIntent]);

  useLayoutEffect(() => {
    if (chat.historyOpen) {
      setHistoryMounted(true);
      const frame = window.requestAnimationFrame(() => setHistoryTransitionOpen(true));
      return () => window.cancelAnimationFrame(frame);
    }
    setHistoryTransitionOpen(false);
    if (!historyMounted) return;
    const duration = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      ? HISTORY_DRAWER_REDUCED_TRANSITION_MS
      : HISTORY_DRAWER_TRANSITION_MS;
    const timeout = window.setTimeout(() => setHistoryMounted(false), duration);
    return () => window.clearTimeout(timeout);
  }, [chat.historyOpen, historyMounted]);

  const closeHistory = useCallback(
    (restoreFocus = true) => {
      historyFocusReturnRef.current = restoreFocus ? 'trigger' : 'messages';
      setHistoryTransitionOpen(false);
      setHistoryOpen(false);
    },
    [setHistoryOpen],
  );

  useEffect(() => {
    if (!chat.historyOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (
        !target ||
        historyRef.current?.contains(target) ||
        historyTriggerRef.current?.contains(target) ||
        isHistoryOwnedOverlayTarget(target)
      ) {
        return;
      }
      closeHistory();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [chat.historyOpen, closeHistory]);

  function handleSurfaceKeyDown(event: KeyboardEvent<HTMLElement>) {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const nestedOverlay = target?.closest('[role="dialog"], [role="menu"], [role="listbox"]');
    if (
      nestedOverlay &&
      nestedOverlay !== surfaceRef.current &&
      !historyRef.current?.contains(target)
    ) {
      return;
    }
    if (event.key === 'Tab' && narrow) {
      trapFocusTab(event, chat.historyOpen ? historyRef.current : surfaceRef.current);
      return;
    }
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    if (chat.historyOpen) {
      closeHistory();
      return;
    }
    if (expanded) {
      setExpandedWithFlip(false);
      requestTargetFocus(true, () => expandTriggerRef.current);
      return;
    }
    restoreLauncherFocusRef.current = true;
    chat.setOpen(false);
  }

  useLayoutEffect(() => {
    if (!chat.open) {
      historyFocusReturnRef.current = null;
      if (restoreLauncherFocusRef.current) {
        restoreLauncherFocusRef.current = false;
        return requestTargetFocus(false, () => launcherRef.current);
      }
      return undefined;
    }
    if (chat.historyOpen) {
      if (!historyTransitionOpen) return undefined;
      return requestTargetFocus(true, () => historyCloseRef.current);
    }
    if (historyMounted) return undefined;
    const historyFocusReturn = historyFocusReturnRef.current;
    if (historyFocusReturn) {
      historyFocusReturnRef.current = null;
      return requestTargetFocus(true, () => {
        if (historyFocusReturn === 'trigger') {
          return historyTriggerRef.current;
        }
        const messageInput = textareaRef.current;
        return messageInput && !messageInput.disabled ? messageInput : historyTriggerRef.current;
      });
    }
    if (!chat.historyOpen) return requestTargetFocus(true, () => textareaRef.current);
    return undefined;
  }, [
    chat.focusRequest,
    chat.historyOpen,
    chat.open,
    historyMounted,
    historyTransitionOpen,
    requestTargetFocus,
  ]);

  const launcherLabel = chat.busy
    ? t('portal.chat.launcher.processing')
    : chat.lastOutcome === 'error'
      ? t('portal.chat.launcher.error')
      : chat.unreadCount > 0
        ? t('portal.chat.launcher.unread', { count: chat.unreadCount })
        : chat.open
          ? t('portal.chat.launcher.collapse')
          : t('portal.chat.launcher.open');

  function handlePresenceTransitionEnd(event: ReactTransitionEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget || event.propertyName !== 'opacity') return;
    const pending = focusIntentRef.current.pending;
    if (!pending) return;
    schedulePendingFocusIntent(pending.generation);
  }

  return (
    <>
      <div
        className="global-chat-presence"
        data-open={chat.open ? 'true' : 'false'}
        onTransitionEnd={handlePresenceTransitionEnd}
      >
        <Card
          ref={surfaceRef}
          id="global-chat-surface"
          className="global-chat-surface shadow-lg"
          data-global-chat-flip="surface"
          data-open={chat.open ? 'true' : 'false'}
          data-layout={narrow ? 'narrow' : 'desktop'}
          data-expanded={expanded ? 'true' : 'false'}
          data-history-mounted={historyMounted ? 'true' : 'false'}
          data-history-open={historyTransitionOpen ? 'true' : 'false'}
          inert={!chat.open ? true : undefined}
          role="dialog"
          aria-hidden={!chat.open ? true : undefined}
          aria-modal={chat.open && narrow && !chat.historyOpen ? true : undefined}
          aria-label={t('portal.chat.panelLabel')}
          onKeyDownCapture={handleSurfaceKeyDown}
        >
          {historyMounted ? (
            <ChatHistory
              narrow={narrow}
              open={historyTransitionOpen}
              historyRef={historyRef}
              closeButtonRef={historyCloseRef}
              onClose={closeHistory}
            />
          ) : null}
          <section
            className="global-chat-panel"
            inert={narrow && chat.historyOpen ? true : undefined}
            aria-hidden={narrow && chat.historyOpen ? true : undefined}
          >
            <header className="global-chat-panel__header" data-global-chat-flip="header">
              <div className="global-chat-panel__leading">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('portal.chat.panel.new')}
                  title={t('portal.chat.panel.new')}
                  onClick={chat.startNewConversation}
                >
                  <Plus aria-hidden="true" />
                </Button>
                <Button
                  ref={historyTriggerRef}
                  type="button"
                  variant={chat.historyOpen ? 'secondary' : 'ghost'}
                  size="default"
                  className="global-chat-history-trigger"
                  aria-label={
                    chat.historyOpen
                      ? t('portal.chat.history.close')
                      : t('portal.chat.history.open')
                  }
                  aria-controls="global-chat-history"
                  aria-expanded={chat.historyOpen}
                  title={
                    chat.historyOpen
                      ? t('portal.chat.history.close')
                      : t('portal.chat.history.open')
                  }
                  onClick={() => {
                    if (chat.historyOpen) {
                      closeHistory();
                    } else {
                      chat.setHistoryOpen(true);
                    }
                  }}
                >
                  <span
                    className="global-chat-single-line"
                    title={chat.selectedConversation?.title}
                  >
                    {chat.selectedConversation?.title ?? t('portal.chat.history.new')}
                  </span>
                  <ChevronDown aria-hidden="true" />
                </Button>
              </div>
              <div className="global-chat-panel__actions">
                <Button
                  ref={expandTriggerRef}
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={
                    expanded ? t('portal.chat.panel.restore') : t('portal.chat.panel.expand')
                  }
                  aria-controls="global-chat-surface"
                  aria-expanded={expanded}
                  title={expanded ? t('portal.chat.panel.restore') : t('portal.chat.panel.expand')}
                  onClick={() => {
                    toggleExpandedWithFlip();
                    requestTargetFocus(true, () => expandTriggerRef.current);
                  }}
                >
                  {expanded ? <Minimize2 aria-hidden="true" /> : <Maximize2 aria-hidden="true" />}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('portal.chat.panel.collapse')}
                  title={t('portal.chat.panel.collapse')}
                  onClick={() => {
                    restoreLauncherFocusRef.current = true;
                    chat.setOpen(false);
                  }}
                >
                  <Minus aria-hidden="true" />
                </Button>
              </div>
            </header>
            <Separator />
            <div className="global-chat-panel__content">
              <div className="global-chat-panel__messages" data-global-chat-flip="messages">
                <ChatMessages />
              </div>
              <ChatComposer textareaRef={textareaRef} fileInputRef={fileInputRef} />
            </div>
          </section>
        </Card>
      </div>

      {/* 52px 触屏入口无法由锁定的共享 Button size 表达，因此保留产品专用语义按钮。 */}
      <button
        ref={launcherRef}
        type="button"
        className="global-chat-launcher"
        data-open={chat.open ? 'true' : 'false'}
        data-status={
          chat.busy
            ? 'processing'
            : chat.lastOutcome === 'error'
              ? 'error'
              : chat.unreadCount > 0
                ? 'unread'
                : 'idle'
        }
        inert={chat.open ? true : undefined}
        tabIndex={chat.open ? -1 : 0}
        aria-hidden={chat.open ? true : undefined}
        aria-label={launcherLabel}
        title={launcherLabel}
        aria-expanded={chat.open}
        aria-controls="global-chat-surface"
        aria-haspopup="dialog"
        onClick={() => chat.setOpen(true)}
      >
        {chat.busy ? (
          <LoaderCircle className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
        ) : chat.lastOutcome === 'error' ? (
          <AlertCircle aria-hidden="true" />
        ) : (
          <MessageCircle aria-hidden="true" />
        )}
        {chat.unreadCount > 0 ? (
          <Badge variant="destructive" className="global-chat-launcher__badge" aria-hidden="true">
            {Math.min(99, chat.unreadCount)}
          </Badge>
        ) : null}
      </button>
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {launcherLabel}
      </span>
    </>
  );
}

function isHistoryOwnedOverlayTarget(target: Element): boolean {
  if (target.closest(CHAT_HISTORY_OWNED_OVERLAY_SELECTOR)) return true;
  return Boolean(
    target.closest('[data-slot="dialog-overlay"]') &&
    document.querySelector(`${CHAT_HISTORY_OWNED_OVERLAY_SELECTOR}[data-slot="dialog-content"]`),
  );
}

function ChatHistory({
  narrow,
  open,
  historyRef,
  closeButtonRef,
  onClose,
}: {
  narrow: boolean;
  open: boolean;
  historyRef: RefObject<HTMLElement | null>;
  closeButtonRef: RefObject<HTMLButtonElement | null>;
  onClose: (restoreFocus?: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const chat = useChat();
  const [renameTarget, setRenameTarget] = useState<{ id: string; title: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [renameTitle, setRenameTitle] = useState('');
  const [actionError, setActionError] = useState<'rename' | 'delete' | null>(null);

  function chooseConversation(conversationId: string) {
    const currentConversation = conversationId === chat.selectedConversationId;
    const selection = chat.selectConversation(conversationId);
    if (currentConversation) {
      onClose(false);
      return;
    }
    void selection.then((selected) => {
      if (selected) onClose(false);
    });
  }

  function createConversation() {
    chat.startNewConversation();
    if (narrow) onClose(false);
  }

  async function confirmRename() {
    if (!renameTarget || !renameTitle.trim()) return;
    const renamed = await chat.renameConversation(renameTarget.id, renameTitle.trim());
    if (renamed) {
      setRenameTarget(null);
      setActionError(null);
    } else {
      setActionError('rename');
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    const deleted = await chat.deleteConversation(deleteTarget.id);
    if (deleted) {
      setDeleteTarget(null);
      setActionError(null);
    } else {
      setActionError('delete');
    }
  }

  return (
    <>
      <aside
        ref={historyRef}
        id="global-chat-history"
        className="global-chat-history"
        data-open={open ? 'true' : 'false'}
        inert={!open ? true : undefined}
        aria-hidden={!open ? true : undefined}
        role={narrow ? 'dialog' : 'region'}
        aria-modal={narrow ? true : undefined}
        aria-label={t('portal.chat.history.label')}
      >
        <header className="global-chat-history__header">
          <Button
            ref={closeButtonRef}
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('portal.chat.history.close')}
            onClick={() => onClose()}
          >
            <History aria-hidden="true" />
          </Button>
        </header>
        <Separator />
        <div className="global-chat-history__body">
          <Button
            type="button"
            variant="outline"
            size="default"
            className="global-chat-history__new"
            onClick={createConversation}
          >
            <Plus aria-hidden="true" />
            <span className="global-chat-single-line">{t('portal.chat.history.new')}</span>
          </Button>
          {chat.conversationSelectionError ? (
            <p className="global-chat-feedback" role="alert">
              {t('portal.chat.messages.loadError')}
            </p>
          ) : null}
          {chat.conversationsLoading ? (
            <HistorySkeleton />
          ) : chat.conversationsError ? (
            <HistoryFeedback
              message={t('portal.chat.history.loadError')}
              action={t('portal.chat.action.retry')}
              onAction={chat.retryConversations}
            />
          ) : chat.conversations.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <History aria-hidden="true" />
                </EmptyMedia>
                <EmptyTitle>{t('portal.chat.history.empty')}</EmptyTitle>
              </EmptyHeader>
            </Empty>
          ) : (
            <div className="global-chat-history__list">
              {chat.conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className="global-chat-history__item"
                  data-active={conversation.id === chat.selectedConversationId ? 'true' : 'false'}
                >
                  <Button
                    type="button"
                    variant={
                      conversation.id === chat.selectedConversationId ? 'secondary' : 'ghost'
                    }
                    size="default"
                    className="global-chat-history__select"
                    aria-current={
                      conversation.id === chat.selectedConversationId ? 'true' : undefined
                    }
                    onClick={() => chooseConversation(conversation.id)}
                  >
                    <span className="global-chat-history__title" title={conversation.title}>
                      {conversation.title}
                    </span>
                    <small className="global-chat-history__time">
                      {new Intl.DateTimeFormat(i18n.language, {
                        month: 'short',
                        day: 'numeric',
                      }).format(new Date(conversation.last_activity_at))}
                    </small>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      render={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t('portal.chat.history.actions', {
                            title: conversation.title,
                          })}
                          disabled={chat.historyMutationId === conversation.id}
                        />
                      }
                    >
                      {chat.historyMutationId === conversation.id ? (
                        <Spinner aria-hidden="true" />
                      ) : (
                        <MoreHorizontal aria-hidden="true" />
                      )}
                    </DropdownMenuTrigger>
                    <DropdownMenuContent data-global-chat-history-owned-overlay align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          setRenameTarget({ id: conversation.id, title: conversation.title });
                          setRenameTitle(conversation.title);
                          setActionError(null);
                        }}
                      >
                        <Pencil aria-hidden="true" />
                        {t('portal.chat.history.rename')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => {
                          setDeleteTarget({ id: conversation.id, title: conversation.title });
                          setActionError(null);
                        }}
                      >
                        <Trash2 aria-hidden="true" />
                        {t('portal.chat.history.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
              {chat.conversationsHasMore ? (
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  disabled={chat.conversationsFetchingMore}
                  onClick={chat.loadMoreConversations}
                >
                  {chat.conversationsFetchingMore ? <Spinner aria-hidden="true" /> : null}
                  {t('portal.chat.history.more')}
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </aside>

      <Dialog
        open={renameTarget !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setRenameTarget(null);
        }}
      >
        <DialogContent data-global-chat-history-owned-overlay>
          <DialogHeader>
            <DialogTitle>{t('portal.chat.history.rename')}</DialogTitle>
            <DialogDescription>{t('portal.chat.history.renameDescription')}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={renameTitle}
            rows={2}
            maxLength={120}
            aria-label={t('portal.chat.history.renameLabel')}
            onChange={(event) => setRenameTitle(event.target.value)}
          />
          {actionError === 'rename' ? (
            <p className="global-chat-feedback" role="alert">
              {t('portal.chat.history.renameError')}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRenameTarget(null)}>
              {t('portal.chat.action.cancel')}
            </Button>
            <Button
              type="button"
              disabled={!renameTitle.trim() || chat.historyMutationId !== null}
              onClick={() => void confirmRename()}
            >
              {chat.historyMutationId ? <Spinner aria-hidden="true" /> : null}
              {t('portal.chat.action.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteTarget(null);
        }}
      >
        <DialogContent data-global-chat-history-owned-overlay>
          <DialogHeader>
            <DialogTitle>{t('portal.chat.history.delete')}</DialogTitle>
            <DialogDescription>
              {t('portal.chat.history.deleteDescription', { title: deleteTarget?.title ?? '' })}
            </DialogDescription>
          </DialogHeader>
          {actionError === 'delete' ? (
            <p className="global-chat-feedback" role="alert">
              {t('portal.chat.history.deleteError')}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
              {t('portal.chat.action.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={chat.historyMutationId !== null}
              onClick={() => void confirmDelete()}
            >
              {chat.historyMutationId ? <Spinner aria-hidden="true" /> : null}
              {t('portal.chat.history.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ChatMessages() {
  const { t, i18n } = useTranslation();
  const chat = useChat();
  const storedMessageById = new Map(chat.historyMessages.map((message) => [message.id, message]));

  if (chat.messagesLoading && chat.messages.length === 0) return <MessageSkeleton />;
  if (chat.messagesError && chat.messages.length === 0) {
    return (
      <HistoryFeedback
        message={t('portal.chat.messages.loadError')}
        action={t('portal.chat.action.retry')}
        onAction={chat.retryMessages}
      />
    );
  }

  return (
    <MessageScrollerProvider autoScroll defaultScrollPosition="end">
      <MessageScroller className="global-chat-message-scroller">
        <MessageScrollerViewport
          className="global-chat-message-viewport"
          tabIndex={0}
          aria-label={t('portal.chat.messages.label')}
        >
          <MessageScrollerContent
            className="global-chat-messages"
            aria-label={t('portal.chat.messages.label')}
            aria-live="polite"
            aria-relevant="additions text"
          >
            {chat.messagesHasMore ? (
              <Button
                type="button"
                variant="outline"
                size="default"
                className="global-chat-messages__more"
                disabled={chat.messagesFetchingMore}
                onClick={chat.loadMoreMessages}
              >
                {chat.messagesFetchingMore ? <Spinner aria-hidden="true" /> : null}
                {t('portal.chat.messages.older')}
              </Button>
            ) : null}
            {chat.messages.length === 0 ? (
              <Empty className="global-chat-empty">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <MessageCircle aria-hidden="true" />
                  </EmptyMedia>
                  <EmptyTitle>{t('portal.chat.empty.title')}</EmptyTitle>
                  <EmptyDescription>{t('portal.chat.empty.body')}</EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              chat.messages.map((message, index) => {
                const previousUser = findPreviousUserMessage(chat.messages, index);
                return (
                  <MessageScrollerItem
                    key={message.id}
                    messageId={message.id}
                    scrollAnchor={index === chat.messages.length - 1}
                  >
                    <ChatMessage
                      message={message}
                      previousUser={previousUser}
                      storedMessage={storedMessageById.get(message.id) ?? null}
                      locale={i18n.language}
                    />
                  </MessageScrollerItem>
                );
              })
            )}
          </MessageScrollerContent>
        </MessageScrollerViewport>
        <MessageScrollerButton
          className="global-chat-message-scroll-button"
          aria-label={t('portal.chat.messages.toBottom')}
        >
          <ChevronDown aria-hidden="true" />
          <span className="sr-only">{t('portal.chat.messages.toBottom')}</span>
        </MessageScrollerButton>
      </MessageScroller>
    </MessageScrollerProvider>
  );
}

function ChatMessage({
  message,
  previousUser,
  storedMessage,
  locale,
}: {
  message: ChatDisplayMessage;
  previousUser: ChatDisplayMessage | null;
  storedMessage: AiChatStoredMessage | null;
  locale: string;
}) {
  const { t } = useTranslation();
  const chat = useChat();
  const assistant = message.role === 'assistant';
  const failed = assistant && message.status === 'failed';
  const processing = assistant && message.status === 'processing';
  const modelId = message.modelId ?? message.requestedModelId ?? message.response?.model_id;
  const modelLabel = assistant
    ? (chat.modelOptions.find((model) => model.id === modelId)?.display_name ??
      t('portal.chat.model.unknown'))
    : null;
  const copyAction = useTimedCopyAction();
  const responseDetails = resolveChatResponseDetails(message, storedMessage);
  const settledPoints = responseDetails.settledPoints;
  const hasResponseDetails =
    assistant &&
    !failed &&
    !processing &&
    responseDetails.settlementStatus === 'settled' &&
    settledPoints !== null;
  const durationMs = assistant ? responseDurationMs(message) : null;
  const durationLabel =
    durationMs === null
      ? null
      : t('portal.chat.result.duration', {
          duration: formatChatDuration(durationMs, locale),
        });
  const canResumeFailure = Boolean(failed && message.local && chat.activeRun?.recoveryRequired);
  const handleFailureRetry = () => {
    if (canResumeFailure) {
      chat.resumeActiveRun();
      return;
    }
    chat.prepareRetry(message, previousUser);
  };
  const messageMeta = assistant ? (
    <div className="global-chat-message__meta">
      <span
        className="global-chat-message__model global-chat-single-line"
        title={modelLabel ?? undefined}
      >
        {modelLabel}
      </span>
      {failed ? (
        <>
          <span className="global-chat-message__separator" aria-hidden="true">
            ·
          </span>
          <Button type="button" variant="ghost" size="xs" onClick={handleFailureRetry}>
            <RotateCcw aria-hidden="true" />
            {t('portal.chat.action.retry')}
          </Button>
        </>
      ) : hasResponseDetails && settledPoints !== null ? (
        <>
          <span className="global-chat-message__separator" aria-hidden="true">
            ·
          </span>
          <ChatResponseDetailsTrigger settledPoints={settledPoints} locale={locale} />
        </>
      ) : null}
    </div>
  ) : null;
  const messageBody = (
    <>
      {message.attachments.length > 0 ? (
        <MessageAttachments attachments={message.attachments} locale={locale} />
      ) : null}
      {processing && !message.content ? (
        <div className="global-chat-message__pending" role="status">
          <Spinner className="motion-reduce:animate-none" aria-hidden="true" />
          <span className="global-chat-single-line">{t('portal.chat.status.processingBody')}</span>
        </div>
      ) : null}
      {message.content ? (
        assistant ? (
          <ChatMarkdown content={message.content} />
        ) : (
          <p className="global-chat-message__user-text">{message.content}</p>
        )
      ) : null}
      {failed ? <ChatFailure message={message} /> : null}
    </>
  );
  const copyButton =
    assistant && message.content && !failed && !processing ? (
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        className="global-chat-message__copy"
        aria-label={
          copyAction.state === 'copied'
            ? t('portal.chat.result.copied')
            : t('portal.chat.result.copy')
        }
        title={
          copyAction.state === 'copied'
            ? t('portal.chat.result.copied')
            : t('portal.chat.result.copy')
        }
        disabled={copyAction.state === 'copying'}
        onClick={() => void copyAction.copy(() => message.content ?? '')}
      >
        {copyAction.state === 'copied' ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      </Button>
    ) : null;
  const messageActions =
    assistant && !failed && !processing && (durationLabel || copyButton) ? (
      <div className="global-chat-message__actions">
        {durationLabel ? (
          <span className="global-chat-message__duration">{durationLabel}</span>
        ) : null}
        {copyButton}
      </div>
    ) : null;

  return (
    <article
      className={`global-chat-message global-chat-message--${message.role}`}
      aria-label={assistant ? t('portal.chat.message.assistant') : t('portal.chat.message.you')}
    >
      <div className="global-chat-message__content">
        {hasResponseDetails ? (
          <Collapsible>
            {messageMeta}
            {messageBody}
            <ChatResponseDetailsContent
              details={responseDetails}
              modelLabel={modelLabel}
              locale={locale}
            />
            {messageActions}
          </Collapsible>
        ) : (
          <>
            {messageMeta}
            {messageBody}
            {messageActions}
          </>
        )}
      </div>
    </article>
  );
}

function useTimedCopyAction() {
  const action = useCopyAction();

  useEffect(() => {
    if (action.state !== 'copied' && action.state !== 'error') return;
    const timer = window.setTimeout(action.reset, CHAT_COPY_FEEDBACK_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [action.reset, action.state]);

  return action;
}

function responseDurationMs(message: ChatDisplayMessage): number | null {
  if (message.status !== 'succeeded' || !message.content?.trim()) return null;
  if (message.startedAt === null || message.completedAt === null) return null;
  const durationMs = message.completedAt - message.startedAt;
  return Number.isFinite(durationMs) && durationMs > 0 ? durationMs : null;
}

function formatChatDuration(durationMs: number, locale: string): string {
  const milliseconds = Math.max(0, Math.round(durationMs));
  if (milliseconds < 1_000) return `${milliseconds}ms`;

  const seconds = milliseconds / 1_000;
  const format = (value: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value);
  if (seconds < 60) return `${format(seconds)}s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds - minutes * 60;
  return `${minutes}m ${format(remainingSeconds)}s`;
}

function ChatFailure({ message }: { message: ChatDisplayMessage }) {
  const { t } = useTranslation();
  const chat = useChat();
  const code = message.error?.code ?? 'chat.failed';
  const kind = chatFailureKind(code);
  const errorDetails = message.local ? chat.activeRun?.errorDetails : null;
  const showBillingAction = kind === 'insufficient_points' || kind === 'payment_risk';

  return (
    <div className="global-chat-failure" role="alert">
      <div className="global-chat-failure__copy">
        <p>{t(`portal.chat.error.${kind}`)}</p>
        {errorDetails ? (
          <small>
            {t('portal.chat.error.pointsDetail', {
              required: errorDetails.required_points ?? '-',
              available: errorDetails.available_points ?? '-',
            })}
          </small>
        ) : null}
      </div>
      {showBillingAction ? (
        <div className="global-chat-failure__actions">
          <Button variant="link" size="sm" nativeButton={false} render={<Link to="/billing" />}>
            {t('portal.chat.action.viewBilling')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function ChatComposer({
  textareaRef,
  fileInputRef,
}: {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const chat = useChat();
  const [textareaNode, setTextareaNode] = useState<HTMLTextAreaElement | null>(null);
  const setTextareaRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      setTextareaNode(node);
    },
    [textareaRef],
  );

  useLayoutEffect(() => {
    const textarea = textareaNode;
    if (!textarea) return;

    let frame: number | null = null;
    let disposed = false;

    const measure = () => {
      frame = null;
      if (disposed || textareaRef.current !== textarea || !textarea.isConnected) return;
      resizeChatTextarea(textarea);
    };
    const schedule = () => {
      if (disposed || frame !== null) return;
      frame = window.requestAnimationFrame(measure);
    };

    schedule();

    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => schedule());
    observer?.observe(textarea);
    return () => {
      disposed = true;
      observer?.disconnect();
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
        frame = null;
      }
    };
  }, [chat.composer.draft, chat.open, textareaNode, textareaRef]);

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    chat.send();
  }

  const composerError = chat.composer.errorCode
    ? t(`portal.chat.composer.error.${chat.composer.errorCode}`, {
        maxCount: chat.attachmentMaxCount,
        defaultValue: t('portal.chat.composer.error.unknown'),
      })
    : null;
  const attachmentError = chat.attachmentIssue
    ? attachmentIssueLabel(chat.attachmentIssue, t, i18n.language)
    : null;

  return (
    <form
      className="global-chat-composer__form"
      aria-busy={chat.busy}
      onSubmit={(event) => {
        event.preventDefault();
        chat.send();
      }}
    >
      <input
        ref={fileInputRef}
        className="sr-only"
        type="file"
        multiple
        tabIndex={-1}
        onChange={(event) => {
          const files = event.target.files ? [...event.target.files] : [];
          event.target.value = '';
          if (files.length > 0) void chat.addAttachments(files);
        }}
      />
      <InputGroup className="global-chat-composer" data-global-chat-flip="composer">
        {chat.composer.attachments.length > 0 ? (
          <InputGroupAddon align="block-start" className="global-chat-composer__body">
            <AttachmentGroup className="global-chat-composer__attachments">
              {chat.composer.attachments.map((attachment) => (
                <ComposerAttachment
                  key={attachment.localId}
                  attachment={attachment}
                  locale={i18n.language}
                />
              ))}
            </AttachmentGroup>
          </InputGroupAddon>
        ) : null}

        <InputGroupTextarea
          ref={setTextareaRef}
          className="global-chat-composer__textarea"
          data-global-chat-flip="textarea"
          value={chat.composer.draft}
          rows={1}
          maxLength={32_000}
          disabled={chat.busy}
          aria-label={t('portal.chat.composer.label')}
          placeholder={t('portal.chat.composer.placeholder')}
          onChange={(event) => chat.setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
        />

        <InputGroupAddon align="block-end" className="global-chat-composer__footer">
          <div className="global-chat-composer__controls">
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <InputGroupButton
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    disabled={chat.busy || !chat.selectedModel || chat.attachmentMaxCount === 0}
                    aria-label={t('portal.chat.attachment.add')}
                    title={t('portal.chat.attachment.add')}
                  />
                }
              >
                <Plus aria-hidden="true" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top" sideOffset={8}>
                <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                  <FileImage aria-hidden="true" />
                  {t('portal.chat.attachment.menu.file')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <InputGroupButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="global-chat-model-trigger"
                    disabled={chat.busy || chat.modelsLoading || chat.modelOptions.length === 0}
                    aria-label={t('portal.chat.model.label')}
                  />
                }
              >
                <span className="global-chat-single-line" title={chat.selectedModel?.display_name}>
                  {chat.selectedModel?.display_name ?? t('portal.chat.model.loading')}
                </span>
                <ChevronDown aria-hidden="true" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                side="top"
                sideOffset={8}
                aria-label={t('portal.chat.model.label')}
                data-global-chat-model-menu
              >
                <DropdownMenuRadioGroup
                  value={chat.selectedModel?.id ?? ''}
                  onValueChange={(modelId) => chat.setModelId(modelId)}
                >
                  {chat.modelOptions.map((model) => (
                    <DropdownMenuRadioItem key={model.id} value={model.id} closeOnClick>
                      <span className="global-chat-model-option" title={model.display_name}>
                        <span className="global-chat-model-option__name">{model.display_name}</span>
                      </span>
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <InputGroupButton
              type="submit"
              variant="default"
              size="icon-sm"
              className="global-chat-send-button"
              disabled={!chat.canSend}
              aria-label={
                chat.busy ? t('portal.chat.composer.sending') : t('portal.chat.composer.send')
              }
              title={chat.busy ? t('portal.chat.composer.sending') : t('portal.chat.composer.send')}
            >
              {chat.busy ? <Spinner aria-hidden="true" /> : <ArrowUp aria-hidden="true" />}
            </InputGroupButton>
          </div>

          {composerError || attachmentError ? (
            <p className="global-chat-feedback global-chat-single-line" role="alert">
              {attachmentError ?? composerError}
            </p>
          ) : null}
          {chat.modelsError ? (
            <p className="global-chat-feedback global-chat-feedback--action" role="alert">
              <span className="global-chat-single-line">{t('portal.chat.model.loadError')}</span>
              <Button type="button" variant="link" size="sm" onClick={chat.retryModels}>
                {t('portal.chat.action.retry')}
              </Button>
            </p>
          ) : null}
        </InputGroupAddon>
      </InputGroup>
    </form>
  );
}

function resizeChatTextarea(textarea: HTMLTextAreaElement) {
  textarea.style.height = 'auto';
  const contentHeight = textarea.scrollHeight;
  const height = Math.min(
    Math.max(contentHeight, CHAT_COMPOSER_TEXTAREA_MIN_HEIGHT),
    CHAT_COMPOSER_TEXTAREA_MAX_HEIGHT,
  );
  textarea.style.height = `${height}px`;
  textarea.style.overflowY = contentHeight > CHAT_COMPOSER_TEXTAREA_MAX_HEIGHT ? 'auto' : 'hidden';
}

function ComposerAttachment({
  attachment,
  locale,
}: {
  attachment: ChatAttachmentItem;
  locale: string;
}) {
  const { t } = useTranslation();
  const chat = useChat();
  const actionPending = chat.attachmentActionIds.has(attachment.localId);
  const canRetryUpload = attachment.errorCode !== 'chat.attachment_delete_failed';
  const state =
    attachment.status === 'uploading'
      ? 'uploading'
      : attachment.status === 'error'
        ? 'error'
        : 'done';

  return (
    <Attachment state={state} size="sm" className="global-chat-attachment">
      <AttachmentMedia>{attachmentIcon(attachment.descriptor)}</AttachmentMedia>
      <AttachmentContent>
        <AttachmentTitle title={attachment.filename}>{attachment.filename}</AttachmentTitle>
        <AttachmentDescription>
          {attachment.status === 'uploading'
            ? t('portal.chat.attachment.uploading')
            : attachment.status === 'error'
              ? t('portal.chat.attachment.failed')
              : formatBytes(attachment.descriptor?.size_bytes ?? attachment.sizeBytes, locale)}
        </AttachmentDescription>
      </AttachmentContent>
      <AttachmentActions>
        {attachment.status === 'error' && canRetryUpload ? (
          <AttachmentAction
            type="button"
            aria-label={t('portal.chat.attachment.retry', { filename: attachment.filename })}
            disabled={actionPending}
            onClick={() => void chat.retryAttachment(attachment.localId)}
          >
            <RefreshCcw aria-hidden="true" />
          </AttachmentAction>
        ) : null}
        <AttachmentAction
          type="button"
          aria-label={t('portal.chat.attachment.remove', { filename: attachment.filename })}
          disabled={attachment.status === 'uploading' || actionPending}
          onClick={() => void chat.removeAttachment(attachment.localId)}
        >
          {actionPending ? <Spinner aria-hidden="true" /> : <X aria-hidden="true" />}
        </AttachmentAction>
      </AttachmentActions>
    </Attachment>
  );
}

function MessageAttachments({
  attachments,
  locale,
}: {
  attachments: readonly AiChatAttachmentDescriptor[];
  locale: string;
}) {
  return (
    <AttachmentGroup>
      {attachments.map((attachment) => (
        <Attachment key={attachment.id} size="sm" className="global-chat-attachment">
          <AttachmentMedia>{attachmentIcon(attachment)}</AttachmentMedia>
          <AttachmentContent>
            <AttachmentTitle title={attachment.filename}>{attachment.filename}</AttachmentTitle>
            <AttachmentDescription>
              {formatBytes(attachment.size_bytes, locale)}
            </AttachmentDescription>
          </AttachmentContent>
        </Attachment>
      ))}
    </AttachmentGroup>
  );
}

function ChatMarkdown({ content }: { content: string }) {
  return (
    <div className="global-chat-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  );
}

const chatMarkdownComponents = {
  a: ({ children, ...props }) => (
    <a {...props} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  ),
  pre: ({ children }) => <MarkdownCodeBlock>{children}</MarkdownCodeBlock>,
} satisfies Components;

function MarkdownCodeBlock({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const copyAction = useTimedCopyAction();
  const text = reactNodeText(children).replace(/\n$/, '');
  return (
    <div className="global-chat-code">
      <div className="global-chat-code__toolbar">
        <Code2 aria-hidden="true" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void copyAction.copy(() => text)}
        >
          {copyAction.state === 'copied' ? <Check aria-hidden="true" /> : null}
          {copyAction.state === 'copied'
            ? t('portal.chat.code.copied')
            : t('portal.chat.code.copy')}
        </Button>
      </div>
      <pre>{children}</pre>
    </div>
  );
}

function ChatResponseDetailsTrigger({
  settledPoints,
  locale,
}: {
  settledPoints: number;
  locale: string;
}) {
  const { t } = useTranslation();
  return (
    <CollapsibleTrigger
      render={<Button type="button" variant="link" size="xs" />}
      className="global-chat-cost-trigger"
    >
      <span className="global-chat-single-line">
        {t('portal.chat.result.points', {
          points: formatNumber(settledPoints, locale),
        })}
      </span>
      <ChevronDown aria-hidden="true" />
    </CollapsibleTrigger>
  );
}

function ChatResponseDetailsContent({
  modelLabel,
  details,
  locale,
}: {
  modelLabel: string | null;
  details: ChatResponseDetails;
  locale: string;
}) {
  const { t } = useTranslation();
  return (
    <CollapsibleContent className="global-chat-cost-details">
      <ResponseFacts
        modelLabel={modelLabel ?? t('portal.chat.model.unknown')}
        details={details}
        locale={locale}
      />
      {details.usage ? <UsageDetails usage={details.usage} locale={locale} /> : null}
      {details.billing ? <BillingDetails billing={details.billing} locale={locale} /> : null}
    </CollapsibleContent>
  );
}

type ChatResponseDetails = {
  settlementStatus: AiChatCompletionResponse['settlement_status'];
  settledPoints: AiChatCompletionResponse['settled_points'];
  billing: AiBillingBreakdown | null;
  usage: AiChatCompletionUsage | null;
};

function resolveChatResponseDetails(
  message: ChatDisplayMessage,
  storedMessage: AiChatStoredMessage | null,
): ChatResponseDetails {
  const historyDetails = storedMessage
    ? {
        settlementStatus: storedMessage.settlement_status,
        settledPoints: storedMessage.settled_points,
        billing: storedMessage.billing,
        usage: storedMessage.usage,
      }
    : null;
  const activeDetails = message.response
    ? {
        settlementStatus: message.response.settlement_status,
        settledPoints: message.response.settled_points,
        billing: message.response.billing,
        usage: message.response.usage,
      }
    : null;

  if (
    historyDetails &&
    (historyDetails.settlementStatus === 'settled' ||
      historyDetails.settlementStatus === 'refunded' ||
      historyDetails.settlementStatus === 'failed' ||
      historyDetails.settlementStatus === 'cancelled')
  ) {
    return historyDetails;
  }
  if (
    activeDetails &&
    (activeDetails.settlementStatus === 'settled' ||
      activeDetails.settlementStatus === 'refunded' ||
      activeDetails.settlementStatus === 'failed' ||
      activeDetails.settlementStatus === 'cancelled')
  ) {
    return activeDetails;
  }
  return (
    historyDetails ??
    activeDetails ?? {
      settlementStatus: null,
      settledPoints: null,
      billing: null,
      usage: null,
    }
  );
}

const tokenUsageKeys = [
  'prompt_tokens',
  'completion_tokens',
  'cache_read_tokens',
  'cache_write_tokens',
  'reasoning_tokens',
  'total_tokens',
] as const;

const billingComponentKeys = [
  'base',
  'input_tokens',
  'output_tokens',
  'cached_tokens',
  'reasoning_tokens',
  'image_input_units',
  'image_output_units',
  'audio_input_units',
  'audio_output_units',
  'tool_calls',
] as const;

function ResponseFacts({
  modelLabel,
  details,
  locale,
}: {
  modelLabel: string;
  details: ChatResponseDetails;
  locale: string;
}) {
  const { t } = useTranslation();
  return (
    <section
      className="global-chat-cost-section"
      aria-label={t('portal.chat.result.responseFacts')}
    >
      <h3>{t('portal.chat.result.responseFacts')}</h3>
      <dl className="global-chat-facts">
        <ChatFact label={t('portal.chat.result.model')} value={modelLabel} />
        <ChatFact
          label={t('portal.chat.result.settlement')}
          value={
            details.settlementStatus
              ? t(`portal.chat.settlement.${details.settlementStatus}`)
              : t('portal.chat.settlement.none')
          }
        />
        <ChatFact
          label={t('portal.chat.result.settledPoints')}
          value={formatNumber(details.settledPoints, locale)}
        />
      </dl>
    </section>
  );
}

function UsageDetails({ usage, locale }: { usage: AiChatCompletionUsage; locale: string }) {
  const { t } = useTranslation();
  return (
    <section className="global-chat-cost-section" aria-label={t('portal.chat.result.usage')}>
      <h3>{t('portal.chat.result.usage')}</h3>
      <dl className="global-chat-facts">
        {tokenUsageKeys.map((key) => (
          <ChatFact
            key={key}
            label={t(`portal.chat.usage.${key}`)}
            value={formatNumber(usage[key], locale)}
          />
        ))}
      </dl>
    </section>
  );
}

function BillingDetails({ billing, locale }: { billing: AiBillingBreakdown; locale: string }) {
  const { t } = useTranslation();
  return (
    <section className="global-chat-cost-section" aria-label={t('portal.chat.result.billing')}>
      <h3>{t('portal.chat.result.billing')}</h3>
      <dl className="global-chat-facts">
        {billingComponentKeys.map((key) => (
          <ChatFact
            key={key}
            label={t(`portal.chat.billing.${key}`)}
            value={`${formatNumber(billing.components_micro_points[key], locale)} ${t(
              'portal.chat.result.microPoints',
            )}`}
          />
        ))}
      </dl>
    </section>
  );
}

function ChatFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="global-chat-fact">
      <dt title={label}>{label}</dt>
      <dd title={value}>{value}</dd>
    </div>
  );
}

function MessageSkeleton() {
  return (
    <div className="global-chat-message-skeleton" aria-hidden="true">
      <Skeleton className="h-20 w-3/4 justify-self-end" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-24 w-4/5" />
    </div>
  );
}

function HistorySkeleton() {
  return (
    <div className="global-chat-history__skeleton" aria-hidden="true">
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-12 w-full" />
      ))}
    </div>
  );
}

function HistoryFeedback({
  message,
  action,
  onAction,
}: {
  message: string;
  action: string;
  onAction: () => void;
}) {
  return (
    <Empty>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <AlertCircle aria-hidden="true" />
        </EmptyMedia>
        <EmptyTitle>{message}</EmptyTitle>
      </EmptyHeader>
      <Button type="button" variant="outline" onClick={onAction}>
        <RefreshCcw aria-hidden="true" />
        {action}
      </Button>
    </Empty>
  );
}

function attachmentIcon(attachment: AiChatAttachmentDescriptor | null) {
  switch (attachment?.kind) {
    case 'image':
      return <FileImage aria-hidden="true" />;
    case 'audio':
      return <FileAudio aria-hidden="true" />;
    case 'video':
      return <FileVideo aria-hidden="true" />;
    case 'code':
      return <FileCode2 aria-hidden="true" />;
    case 'pdf':
    case 'text':
      return <FileText aria-hidden="true" />;
    case 'office':
      return <FileArchive aria-hidden="true" />;
    default:
      return <File aria-hidden="true" />;
  }
}

function attachmentIssueLabel(
  issue: NonNullable<ReturnType<typeof useChat>['attachmentIssue']>,
  t: (key: string, options?: Record<string, unknown>) => string,
  locale: string,
): string {
  return t(`portal.chat.attachment.error.${issue.code}`, {
    filename: issue.filename ?? '-',
    maxCount: issue.maxCount ?? '-',
    maxBytes: issue.maxBytes ? formatBytes(issue.maxBytes, locale) : '-',
    defaultValue: t('portal.chat.attachment.error.unknown'),
  });
}

function findPreviousUserMessage(
  messages: readonly ChatDisplayMessage[],
  currentIndex: number,
): ChatDisplayMessage | null {
  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role === 'user') return message;
  }
  return null;
}

function trapFocusTab(event: KeyboardEvent<HTMLElement>, container: HTMLElement | null) {
  if (!container) return;
  const focusable = [
    ...container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ].filter(
    (element) =>
      !element.hasAttribute('disabled') &&
      element.getAttribute('aria-hidden') !== 'true' &&
      !element.closest('[aria-hidden="true"], [inert]'),
  );
  if (focusable.length === 0) {
    event.preventDefault();
    container.focus();
    return;
  }
  const first = focusable[0]!;
  const last = focusable.at(-1)!;
  const active = document.activeElement;
  if (
    active instanceof HTMLElement &&
    !container.contains(active) &&
    active.closest('[role="dialog"], [role="menu"], [role="listbox"]')
  ) {
    return;
  }
  if (event.shiftKey) {
    if (active === first || !container.contains(active)) {
      event.preventDefault();
      last.focus();
    }
    return;
  }
  if (active === last || !container.contains(active)) {
    event.preventDefault();
    first.focus();
  }
}

function reactNodeText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(reactNodeText).join('');
  if (isValidElement(node)) {
    return reactNodeText((node as ReactElement<{ children?: ReactNode }>).props.children);
  }
  return '';
}

function formatNumber(value: number | null | undefined, locale: string): string {
  return value == null ? '-' : new Intl.NumberFormat(locale).format(value);
}

interface ChatSurfaceRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface ChatFlipSnapshot {
  element: HTMLElement;
  rect: ChatSurfaceRect;
}

function readChatSurfaceRect(element: HTMLElement): ChatSurfaceRect {
  const rect = element.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

function getChatFlipTargets(surface: HTMLElement): HTMLElement[] {
  return [surface, ...surface.querySelectorAll<HTMLElement>(CHAT_FLIP_TARGET_SELECTOR)];
}

function useExpandedChatSurface(surfaceRef: RefObject<HTMLElement | null>) {
  const [expanded, setExpanded] = useState(false);
  const expandedRef = useRef(false);
  const pendingSnapshotsRef = useRef<ChatFlipSnapshot[] | null>(null);
  const animationsRef = useRef(new Map<HTMLElement, Animation>());

  const cancelAnimations = useCallback(() => {
    const animations = [...animationsRef.current.values()];
    animationsRef.current.clear();
    for (const animation of animations) animation.cancel();
  }, []);

  const startFlipAnimations = useCallback(
    (snapshots: ChatFlipSnapshot[]) => {
      const surface = surfaceRef.current;
      if (!surface) return;

      const activeSnapshots = snapshots.filter(
        ({ element }) => element.isConnected && (element === surface || surface.contains(element)),
      );
      const lastRects = new Map(
        activeSnapshots.map(({ element }) => [element, readChatSurfaceRect(element)]),
      );
      const animations: Animation[] = [];

      for (const { element, rect: first } of activeSnapshots) {
        const last = lastRects.get(element);
        if (!last || last.width === 0 || last.height === 0) continue;

        const projected = readChatSurfaceRect(element);
        if (projected.width === 0 || projected.height === 0) continue;
        const ancestorScaleX = projected.width / last.width;
        const ancestorScaleY = projected.height / last.height;
        if (ancestorScaleX === 0 || ancestorScaleY === 0) continue;

        const translateX = (first.left - projected.left) / ancestorScaleX;
        const translateY = (first.top - projected.top) / ancestorScaleY;
        const scaleX = first.width / projected.width;
        const scaleY = first.height / projected.height;
        if (
          Math.abs(translateX) < 0.5 &&
          Math.abs(translateY) < 0.5 &&
          Math.abs(scaleX - 1) < 0.001 &&
          Math.abs(scaleY - 1) < 0.001
        ) {
          continue;
        }

        const animation = element.animate(
          [
            {
              transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scaleX}, ${scaleY})`,
            },
            { transform: 'none' },
          ],
          {
            duration: CHAT_SURFACE_TRANSITION_MS,
            easing: CHAT_SURFACE_EASING,
            fill: 'both',
          },
        );
        animationsRef.current.set(element, animation);
        animations.push(animation);
        animation.pause();
        animation.currentTime = 0;
        animation.onfinish = () => {
          if (animationsRef.current.get(element) !== animation) return;
          animationsRef.current.delete(element);
          animation.cancel();
        };
        animation.oncancel = () => {
          if (animationsRef.current.get(element) === animation) {
            animationsRef.current.delete(element);
          }
        };
      }

      for (const animation of animations) animation.play();
    },
    [surfaceRef],
  );

  const setExpandedWithFlip = useCallback(
    (nextExpanded: boolean) => {
      if (expandedRef.current === nextExpanded) return;

      const surface = surfaceRef.current;
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const targets = surface ? getChatFlipTargets(surface) : [];
      if (
        surface &&
        !reducedMotion &&
        targets.length > 0 &&
        targets.every((element) => typeof element.animate === 'function')
      ) {
        pendingSnapshotsRef.current = targets.map((element) => ({
          element,
          rect: readChatSurfaceRect(element),
        }));
      } else {
        pendingSnapshotsRef.current = null;
      }

      cancelAnimations();
      expandedRef.current = nextExpanded;
      setExpanded(nextExpanded);
    },
    [cancelAnimations, surfaceRef],
  );
  const toggleExpandedWithFlip = useCallback(() => {
    setExpandedWithFlip(!expandedRef.current);
  }, [setExpandedWithFlip]);

  useLayoutEffect(() => {
    const snapshots = pendingSnapshotsRef.current;
    pendingSnapshotsRef.current = null;
    if (snapshots) startFlipAnimations(snapshots);
  }, [expanded, startFlipAnimations]);

  useEffect(() => () => cancelAnimations(), [cancelAnimations]);

  return { expanded, setExpandedWithFlip, toggleExpandedWithFlip };
}

function useNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(CHAT_NARROW_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia(CHAT_NARROW_QUERY);
    const update = () => setNarrow(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return narrow;
}
